"use client";

import { useMemo } from "react";
import { useAuth } from "@/contexts/auth-context";
import { FirstTimeEmptyState } from "./first-time-empty-state";
import { oddsRangeActive } from "../odds-range";
import { type OptimizerQueryState } from "../query-state";

export type OptimizerEmptySurface = "lines" | "fantasy-props";

interface SmartEmptyStateProps {
  page: "optimizer" | "props";
  surface?: OptimizerEmptySurface;
  hasFilters?: boolean;
  filterState?: OptimizerQueryState;
  totalBeforeFilters?: number;
  onResetFilters?: () => void;
  onRetry?: () => void;
}

interface EmptyStateCopy {
  title: string;
  description: string;
  showPeakTimeChart?: boolean;
}

const QUIET_SLATE_COPY: Record<OptimizerEmptySurface, EmptyStateCopy> = {
  lines: {
    title: "Quiet slate right now",
    description:
      "Only a handful of games are posted this early. Lines usually fill in closer to start — the board usually gets busy after ~4pm ET.",
    showPeakTimeChart: true,
  },
  "fantasy-props": {
    title: "Quiet slate right now",
    description:
      "Only a handful of games are posted this early. Props fill in 2–3 hours before start — the board usually gets busy after ~4pm ET.",
    showPeakTimeChart: true,
  },
};

function resolveEmptySurface(
  surface: OptimizerEmptySurface | undefined,
  page: "optimizer" | "props",
): OptimizerEmptySurface {
  if (surface) return surface;
  return page === "props" ? "fantasy-props" : "lines";
}

function leagueEmptyCopy(surface: OptimizerEmptySurface, leagues: string[]): EmptyStateCopy {
  const leagueLabel = formatLeagueFilter(leagues);
  if (surface === "fantasy-props") {
    return {
      title: `No matches on ${leagueLabel} yet`,
      description: "Lines usually post closer to game time. Widen your leagues or check back this evening.",
      showPeakTimeChart: true,
    };
  }
  return {
    title: `No lines on ${leagueLabel} yet`,
    description: "Lines usually post closer to game time. Widen your leagues or check back this evening.",
    showPeakTimeChart: true,
  };
}

function hasActiveServerSideOptimizerFilters(state: OptimizerQueryState): boolean {
  return (
    state.leagues.length === 1 ||
    state.statTypes.length > 0 ||
    state.gameDays.length > 0 ||
    state.startsWithin > 0 ||
    state.minEV > 0 ||
    state.minDataPoints > 0 ||
    state.minOddsToHit > 0 ||
    state.maxOddsToHit < 100 ||
    state.weights !== null
  );
}

function hasActiveClientSideOptimizerFilters(state: OptimizerQueryState): boolean {
  return (
    state.playerSearch.trim() !== "" ||
    state.leagues.length > 1 ||
    state.matchups.length > 0 ||
    state.requiredComparisonBooks.length > 0 ||
    state.requiredBooks.length > 0 ||
    oddsRangeActive(state) ||
    state.hidePushProps ||
    state.overUnder !== "" ||
    state.slipType !== ""
  );
}

function canResetFiltersExplainEmptyState(
  state: OptimizerQueryState | undefined,
  totalBeforeFilters: number | undefined,
): boolean {
  if (!state) return false;
  if (hasActiveServerSideOptimizerFilters(state)) return true;
  return totalBeforeFilters !== undefined && totalBeforeFilters > 0 && hasActiveClientSideOptimizerFilters(state);
}

function hasRestrictiveFilters(state: OptimizerQueryState | undefined): boolean {
  if (!state) return false;
  return hasActiveServerSideOptimizerFilters(state) || hasActiveClientSideOptimizerFilters(state);
}

function formatPercentThreshold(value: number): string {
  const rounded = Number(value.toFixed(1));
  return Number.isInteger(rounded)
    ? rounded.toString()
    : rounded.toFixed(1).replace(/\.0$/, "");
}

function formatLeagueFilter(leagues: string[]): string {
  return leagues
    .map((league) => league.trim())
    .filter(Boolean)
    .map((league) => league.toUpperCase())
    .join(", ");
}

function diagnoseEmptyState(
  state: OptimizerQueryState | undefined,
  totalBeforeFilters: number | undefined,
  surface: OptimizerEmptySurface,
): EmptyStateCopy {
  if (!state) return QUIET_SLATE_COPY[surface];
  if (totalBeforeFilters && totalBeforeFilters > 0) {
    if (state.playerSearch) {
      return {
        title: `No matches for "${state.playerSearch}"`,
        description: "Try a different search or reset filters.",
      };
    }
    return {
      title: "Filters removed all results",
      description: "Try adjusting your filters or reset them to reopen the board.",
    };
  }
  const minEV = state.minEV ?? 0;
  if (minEV > 0) {
    const minEVLabel = formatPercentThreshold(minEV);
    return {
      title: `Nothing clears ${minEVLabel}% right now`,
      description:
        "The market's efficient this early. Drop to 1–2% to catch thinner edges, or check back for the evening slate.",
    };
  }
  if (state.leagues.length > 0) {
    return leagueEmptyCopy(surface, state.leagues);
  }
  if (!hasRestrictiveFilters(state)) {
    return QUIET_SLATE_COPY[surface];
  }
  return {
    title: "Nothing matches these filters",
    description: "Broaden filters or check back when more lines are posted.",
  };
}

export function SmartEmptyState({
  page,
  surface,
  hasFilters = false,
  filterState,
  totalBeforeFilters,
  onResetFilters,
  onRetry,
}: SmartEmptyStateProps) {
  const { subscriptions } = useAuth();
  const isTrialUser = subscriptions.some((s) => s.status === "trialing");
  const resolvedSurface = resolveEmptySurface(surface, page);

  const emptyStateCopy = useMemo(
    () => diagnoseEmptyState(filterState, totalBeforeFilters, resolvedSurface),
    [filterState, resolvedSurface, totalBeforeFilters]
  );
  const filtersActive =
    hasFilters || canResetFiltersExplainEmptyState(filterState, totalBeforeFilters);
  const resetHandler = filtersActive ? onResetFilters : undefined;

  return (
    <FirstTimeEmptyState
      page={page}
      hasFilters={filtersActive}
      title={emptyStateCopy.title}
      description={emptyStateCopy.description}
      totalBeforeFilters={totalBeforeFilters}
      onResetFilters={resetHandler}
      onRetry={onRetry}
      trackingVariant={isTrialUser ? "first_time" : "generic"}
      showPeakTimeChart={emptyStateCopy.showPeakTimeChart}
    />
  );
}
